/**
 * Shared Assessment Public View
 *
 * Displays assessment results via shareable link
 * No authentication required - accessible by anyone with the link
 * Shows full assessment details, recommendations, and charts
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Results from '../components/assessment/Results';
import logo from '../assets/logo.png';

export default function SharedAssessment() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [linkInfo, setLinkInfo] = useState(null);

  useEffect(() => {
    fetchSharedAssessment();
  }, [token]);

  const fetchSharedAssessment = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/.netlify/functions/get-shared-assessment?token=${token}`);
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 410) {
          setError({
            type: 'expired',
            message: 'This assessment link has expired.',
            details: 'Shared links are valid for 30 days. Please contact your HVAC contractor for a new link.'
          });
        } else if (response.status === 403) {
          setError({
            type: 'disabled',
            message: 'This link has been disabled.',
            details: 'Please contact your HVAC contractor for assistance.'
          });
        } else {
          setError({
            type: 'error',
            message: data.error || 'Unable to load assessment',
            details: 'The link may be invalid or the assessment may have been deleted.'
          });
        }
        return;
      }

      setAssessment(data.assessment);
      setLinkInfo(data.link);
    } catch (err) {
      console.error('Error fetching shared assessment:', err);
      setError({
        type: 'error',
        message: 'Failed to load assessment',
        details: 'Please check your internet connection and try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate days until expiration
  const getDaysUntilExpiration = () => {
    if (!linkInfo?.expiresAt) return null;
    const expiration = new Date(linkInfo.expiresAt);
    const now = new Date();
    const diffTime = expiration - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e0e6ed',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading assessment...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc',
        padding: '20px'
      }}>
        <div style={{ maxWidth: '600px', width: '100%' }}>
          <div className="card" style={{ textAlign: 'center', padding: '48px 32px' }}>
            <div style={{
              fontSize: '64px',
              marginBottom: '24px'
            }}>
              {error.type === 'expired' ? '⏰' : error.type === 'disabled' ? '🔒' : '❌'}
            </div>
            <h1 style={{ fontSize: '28px', marginBottom: '12px', color: '#2c3e50' }}>
              {error.message}
            </h1>
            <p style={{ color: '#64748b', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px' }}>
              {error.details}
            </p>
            {error.type === 'expired' && linkInfo?.expiresAt && (
              <div style={{
                fontSize: '14px',
                color: '#94a3b8',
                marginBottom: '24px',
                padding: '12px',
                background: '#f1f5f9',
                borderRadius: '6px'
              }}>
                This link expired on {new Date(linkInfo.expiresAt).toLocaleDateString()}
              </div>
            )}
            <div style={{
              padding: '16px',
              background: '#f0fdf4',
              border: '2px solid #22c55e',
              borderRadius: '8px',
              fontSize: '15px',
              color: '#14532d',
              lineHeight: '1.5'
            }}>
              <strong>Need help?</strong><br />
              Contact your HVAC contractor to receive a new assessment link.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return null;
  }

  const daysLeft = getDaysUntilExpiration();

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', paddingBottom: '60px' }}>
      {/* Public Header */}
      <header style={{
        background: 'white',
        borderBottom: '2px solid #e0e6ed',
        padding: '16px 0',
        marginBottom: '32px'
      }}>
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src={logo} alt="Krinos" style={{ height: '36px', width: 'auto' }} />
            <span style={{
              fontSize: '22px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Krinos
            </span>
          </div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            HVAC Assessment Report
          </div>
        </div>
      </header>

      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        {/* Link Expiration Notice */}
        {daysLeft !== null && daysLeft <= 7 && (
          <div style={{
            padding: '16px 20px',
            background: daysLeft <= 3 ? '#fef2f2' : '#fffbeb',
            border: `2px solid ${daysLeft <= 3 ? '#ef4444' : '#f59e0b'}`,
            borderRadius: '8px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>{daysLeft <= 3 ? '⚠️' : '⏰'}</span>
            <div>
              <strong style={{ color: daysLeft <= 3 ? '#991b1b' : '#92400e' }}>
                This link expires in {daysLeft} {daysLeft === 1 ? 'day' : 'days'}
              </strong>
              <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>
                Save or screenshot this report if you need it for future reference.
              </div>
            </div>
          </div>
        )}

        {/* Customer & Contractor Info */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          {/* Customer Info */}
          {assessment.customers && (
            <div className="card">
              <h3 style={{ fontSize: '18px', marginBottom: '16px', color: '#2c3e50' }}>
                Property Information
              </h3>
              <div style={{ display: 'grid', gap: '8px', fontSize: '14px' }}>
                <div>
                  <strong>{assessment.customers.first_name} {assessment.customers.last_name}</strong>
                </div>
                <div style={{ color: '#64748b' }}>
                  {assessment.customers.address}<br />
                  {assessment.customers.city}, {assessment.customers.state} {assessment.customers.zip_code}
                </div>
              </div>
            </div>
          )}

          {/* Contractor Info */}
          {assessment.contractors && (
            <div className="card">
              <h3 style={{ fontSize: '18px', marginBottom: '16px', color: '#2c3e50' }}>
                Prepared By
              </h3>
              <div style={{ display: 'grid', gap: '8px', fontSize: '14px' }}>
                <div>
                  <strong>{assessment.contractors.company_name}</strong>
                </div>
                {assessment.contractors.phone && (
                  <div style={{ color: '#64748b' }}>
                    📞 {assessment.contractors.phone}
                  </div>
                )}
                {assessment.contractors.email && (
                  <div style={{ color: '#64748b' }}>
                    ✉️ {assessment.contractors.email}
                  </div>
                )}
                {assessment.contractors.website && (
                  <div>
                    <a href={assessment.contractors.website} target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                      🌐 Visit Website
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Assessment Results */}
        <Results
          results={assessment.results}
          state={assessment.form_data}
          isSharedView={true}
        />

        {/* Footer Info */}
        <div style={{
          marginTop: '48px',
          padding: '24px',
          background: '#f1f5f9',
          borderRadius: '12px',
          textAlign: 'center',
          fontSize: '13px',
          color: '#64748b'
        }}>
          <p style={{ marginBottom: '8px' }}>
            This assessment was generated on {new Date(assessment.created_at).toLocaleDateString()} using Krinos HVAC Assessment Platform
          </p>
          <p style={{ margin: 0 }}>
            Assessment ID: {assessment.id.substring(0, 8).toUpperCase()}
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
